"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Linkedin, Instagram, Facebook, Twitter, Send, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function QuickLinksMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="12966" className="space-y-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-24 animate-pulse rounded bg-muted/10" />)}
      </div>;
  }
  const items = menuItems.length > 0 ? menuItems : [{
    id: "home",
    label: "Home",
    href: "/HomePage"
  }, {
    id: "what-we-buy",
    label: "What We Buy",
    href: "/WhatWeBuyPage"
  }, {
    id: "our-story",
    label: "Our Story",
    href: "/OurStoryPage"
  }, {
    id: "contact",
    label: "Contact",
    href: "#"
  }];
  return <ul className="space-y-3">
      {items.map((item, i) => <li key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="group inline-flex items-center gap-2 text-sm font-sans text-muted-foreground transition-all duration-300 hover:-translate-y-1 hover:text-primary">
            <span className="h-px w-0 bg-primary transition-all duration-300 group-hover:w-3" />
            {item.label}
          </Link>
        </li>)}
    </ul>;
}
function NewsletterForm() {
  const {
    handleSubmit,
    control,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="rounded-sm border border-primary/20 bg-primary/10 p-4 text-sm font-sans text-primary">
        {successMessage}
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex gap-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^\S+@\S+$/i,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1">
              <FormControl>
                <Input placeholder="Enter your email" {...field} className="h-10 border-foreground/10 bg-foreground/5 font-sans text-foreground placeholder:text-foreground/40 focus:border-primary focus:ring-primary/20 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <Button type="submit" size="icon" disabled={isSubmitting} className="h-10 w-10 shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm transition-colors duration-300">
          <Send className="size-4" />
        </Button>
      </div>
      <p className="text-xs font-sans text-muted-foreground">
        Subscribe to receive direct cash offer updates and local market insights.
      </p>
    </Form>;
}
export default function CorporateFooter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const services = ["Foreclosure Homes", "Probate Properties", "Distressed Houses", "Inherited Homes", "Rental Properties"];
  const socialLinks = [{
    icon: Linkedin,
    href: "https://linkedin.com",
    label: "LinkedIn"
  }, {
    icon: Instagram,
    href: "https://instagram.com",
    label: "Instagram"
  }, {
    icon: Facebook,
    href: "https://facebook.com",
    label: "Facebook"
  }, {
    icon: Twitter,
    href: "https://twitter.com",
    label: "Twitter"
  }];
  return <section id="footer" ref={ref} className={`dark bg-background text-foreground relative overflow-hidden border-t border-foreground/5 pt-20 pb-8 transition-opacity duration-700 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--primary)_0%,_transparent_30%)] opacity-5" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,_transparent_0%,_var(--background)_100%)] opacity-50" />

      <div className="container relative mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-4 lg:gap-8">
          <div className="space-y-6">
            <Link to="#" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579273/img/new_chapter_logo.png" alt="CashNest" className="h-7 w-auto object-contain opacity-90 transition-opacity hover:opacity-100" />
            </Link>
            <p className="text-sm font-sans leading-relaxed text-muted-foreground">
              We buy houses fast for cash with no hidden fees and we cover closing costs. 
              We help homeowners sell quickly in any condition, ensuring a stress-free 
              transition without realtor commissions.
            </p>
            <div className="space-y-3 pt-2">
              
              <div className="flex items-center gap-3 text-sm font-mono text-muted-foreground">
                <Phone className="size-4 text-primary" />
                <Link to="tel:513-822-5882" className="hover:text-primary transition-colors duration-300">
                  513-822-5882
                </Link>
              </div>
              <div className="flex items-center gap-3 text-sm font-mono text-muted-foreground">
                <Mail className="size-4 text-primary" />
                <Link to="mailto:Darin@newchapterpropertysolutions.com" className="hover:text-primary transition-colors duration-300">
                  Darin@newchapterpropertysolutions.com
                </Link>
              </div>
            </div>
          </div>

          <div className="space-y-6 lg:pl-8">
            <h3 className="text-lg font-bold uppercase tracking-tighter text-foreground">
              Navigation
            </h3>
            <MenuProvider menu_id="50" className="w-full">
              <QuickLinksMenu />
            </MenuProvider>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-bold uppercase tracking-tighter text-foreground">
              What We Buy
            </h3>
            <ul className="space-y-3">
              {services.map((service, i) => <li key={i} data-index={i}>
                  <Link to="#" className="group flex items-center justify-between text-sm font-sans text-muted-foreground transition-all duration-300 hover:-translate-y-1 hover:text-primary">
                    <span>{service}</span>
                    <ArrowUpRight className="size-3 opacity-0 transition-all duration-300 group-hover:translate-x-1 group-hover:opacity-100" />
                  </Link>
                </li>)}
            </ul>
          </div>

          <div className="space-y-6">
            <h3 className="text-lg font-bold uppercase tracking-tighter text-foreground">
              Stay Connected
            </h3>
            <p className="text-sm font-sans text-muted-foreground">
              Join our newsletter for direct cash offer alerts and property investment insights.
            </p>
            <FormProvider formId="footer-newsletter" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="Thank you for subscribing!">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        <Separator className="my-12 bg-foreground/10" />

        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          <div className="text-center text-xs font-mono text-muted-foreground md:text-left">
            <p>© 2010&nbsp;CashNest. All rights reserved.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-xs font-mono text-muted-foreground uppercase tracking-widest">
            <Link to="#" className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <Link to="#" className="hover:text-primary transition-colors">
              Terms of Service
            </Link>
            <Link to="#" className="hover:text-primary transition-colors">
              Sitemap
            </Link>
          </div>

          <div className="flex items-center gap-4">
            {socialLinks.map((social, i) => <Link key={i} data-index={i} to={social.href} newTab className="group flex size-9 items-center justify-center rounded-sm border border-foreground/10 bg-foreground/5 text-muted-foreground transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:bg-primary hover:text-primary-foreground" aria-label={social.label}>
                <social.icon className="size-4" />
              </Link>)}
          </div>
        </div>
      </div>
      <script src="https://widgets.leadconnectorhq.com/loader.js" data-resources-url="https://widgets.leadconnectorhq.com/chat-widget/loader.js" data-widget-id="69d3ebc89a22113b4b2c5062" />
    </section>;
}